import { Auth, Connect } from '@genesislcap/foundation-comms';
import {
  CriteriaSegmentedControl,
  CriteriaSegmentedControlOption,
  Serialisers,
} from '@genesislcap/foundation-criteria';
import { customElement, GenesisElement, observable, inject } from '@genesislcap/web-core';
import { ActivityTodayStyles as styles } from './activity-today.styles';
import { ActivityTodayTemplate as template } from './activity-today.template';
import { TrancheDetailFlyout } from '../bond-bidding/tranche-detail-flyout';
import { GridProStatusBarTypes, LabelValueStatusBarComponent } from '@genesislcap/rapid-grid-pro';
import { formatNumeric } from '../../../../mbm/utils/bond-bid-utils';

const ACTIVITY_TODAY_FILTER_KEY = 'activity-today-filter-state';

interface ActivityTodayFilterSnapshot {
  cusipFilterValue: string;
  referenceFilterValue: string;
  issuerFilterValue: string;
  traderFilterValue: string;
  activityCriteriaValue: string;
}

function filterStorageKey(userName: string): string {
  return `${ACTIVITY_TODAY_FILTER_KEY}:${userName}`;
}

function loadStoredFilters(userName: string): ActivityTodayFilterSnapshot | null {
  try {
    const raw = sessionStorage.getItem(filterStorageKey(userName));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ActivityTodayFilterSnapshot;
    if (
      typeof parsed.cusipFilterValue !== 'string' ||
      typeof parsed.referenceFilterValue !== 'string' ||
      typeof parsed.issuerFilterValue !== 'string' ||
      typeof parsed.traderFilterValue !== 'string' ||
      typeof parsed.activityCriteriaValue !== 'string'
    ) {
      return null;
    }
    return parsed;
  } catch (error) {
    console.warn("Failed to load Today's Activity filters from sessionStorage", error);
    return null;
  }
}

function saveStoredFilters(userName: string, state: ActivityTodayFilterSnapshot): void {
  try {
    sessionStorage.setItem(filterStorageKey(userName), JSON.stringify(state));
  } catch (error) {
    console.warn("Failed to save Today's Activity filters to sessionStorage", error);
  }
}

function clearStoredFilters(userName: string): void {
  try {
    sessionStorage.removeItem(filterStorageKey(userName));
  } catch (error) {
    console.warn("Failed to clear Today's Activity filters from sessionStorage", error);
  }
}

@customElement({
  name: 'activity-today-route',
  template,
  styles,
})
export class ActivityTodayRoute extends GenesisElement {
  private isRestoringFilters = false;

  @Auth private auth: Auth;
  @observable public bondDetailsOpen: boolean = false;
  @observable todaysActivityGridStateKey: string = 'activity-today-grid-state';
  @observable selectedBondId: string | null = null;
  @observable activityCriteria: CriteriaSegmentedControl;
  @observable filterValue: string = '';
  @observable cusipFilterValue: string = '';
  @observable referenceFilterValue: string = '';
  @observable issuerFilterValue: string = '';
  @observable traderFilterValue: string = '';

  trancheDetail: TrancheDetailFlyout;

  private gridApi: any = null;
  private statusRefreshInterval: any = null;

  @observable activityOptions: CriteriaSegmentedControlOption[] = [
    { label: 'Submitted', field: 'BID_STATUS', value: 'INSERT_ACCEPTED', serialiser: Serialisers.EQ },
    { label: 'Cancelled', field: 'BID_STATUS', value: 'CANCELLED', serialiser: Serialisers.EQ },
    { label: 'Executed', field: 'BID_STATUS', value: 'TRADED', serialiser: Serialisers.EQ },
    { label: 'Cover', field: 'BID_STATUS', value: 'COVER', serialiser: Serialisers.EQ },
    { label: 'Traded Away', field: 'BID_STATUS', value: 'TRADED_AWAY', serialiser: Serialisers.EQ },
    { label: 'Passed', field: 'BID_STATUS', value: 'PASSED', serialiser: Serialisers.EQ },
    { label: 'Expired', field: 'BID_STATUS', value: 'EXPIRED', serialiser: Serialisers.EQ },
    { label: 'Rejected', field: 'BID_STATUS', value: 'REJECTED', serialiser: Serialisers.EQ },
  ];

  @inject(Connect) connect: Connect;

  @observable activityCriteriaValue = '';
  activityCriteriaValueChanged() {
    this.onFilterInputsChanged(true);
  }

  cusipFilterValueChanged() {
    this.onFilterInputsChanged(false);
  }

  referenceFilterValueChanged() {
    this.onFilterInputsChanged(false);
  }

  issuerFilterValueChanged() {
    this.onFilterInputsChanged(false);
  }

  traderFilterValueChanged() {
    this.onFilterInputsChanged(false);
  }

  private onFilterInputsChanged(recalculateTotal: boolean) {
    this.rebuildFilterValue();
    if (recalculateTotal) {
      this.calculateTotalQuantity();
    }
    this.persistFiltersIfNeeded();
  }

  private rebuildFilterValue() {
    const parts: string[] = [];
    if (this.activityCriteriaValue) {
      parts.push(this.activityCriteriaValue);
    }
    const cusip = this.criteriaValue(this.cusipFilterValue, true);
    if (cusip) parts.push(`Expr.containsIgnoreCase(CUSIP, "${cusip}")`);
    const reference = this.criteriaValue(this.referenceFilterValue);
    if (reference) parts.push(`Expr.containsIgnoreCase(BOND_ID, "${reference}")`);
    const issuer = this.criteriaValue(this.issuerFilterValue);
    if (issuer) parts.push(`Expr.containsIgnoreCase(ISSUER_NAME, "${issuer}")`);
    const trader = this.criteriaValue(this.traderFilterValue);
    if (trader) parts.push(`Expr.containsIgnoreCase(TRADER, "${trader}")`);
    this.filterValue = parts.join(' && ');
  }

  private criteriaValue(value: string, uppercase = false): string {
    const trimmed = value?.trim().replace(/["']/g, '') ?? '';
    return uppercase ? trimmed.toUpperCase() : trimmed;
  }

  private persistFiltersIfNeeded(): void {
    if (!this.isConnected || this.isRestoringFilters) return;
    const userName = this.auth?.currentUser?.userName || 'anonymous';
    saveStoredFilters(userName, {
      cusipFilterValue: this.cusipFilterValue,
      referenceFilterValue: this.referenceFilterValue,
      issuerFilterValue: this.issuerFilterValue,
      traderFilterValue: this.traderFilterValue,
      activityCriteriaValue: this.activityCriteriaValue,
    });
  }

  private applyStoredFilters(state: ActivityTodayFilterSnapshot): void {
    this.isRestoringFilters = true;
    this.cusipFilterValue = state.cusipFilterValue;
    this.referenceFilterValue = state.referenceFilterValue;
    this.issuerFilterValue = state.issuerFilterValue;
    this.traderFilterValue = state.traderFilterValue;
    this.activityCriteriaValue = state.activityCriteriaValue;
    this.rebuildFilterValue();
    this.isRestoringFilters = false;
  }

  async connectedCallback() {
    const userName = this.auth?.currentUser?.userName || 'anonymous';
    const saved = loadStoredFilters(userName);
    if (saved) {
      this.applyStoredFilters(saved);
    }
    super.connectedCallback();
  }

  public gridOptions = {
    // BID_ID is stable; reconnect can replay the same ROW_REF as an insert.
    getRowId: (params) => {
      const bidId = params.data?.BID_ID;
      if (bidId != null && bidId !== '') {
        return String(bidId);
      }
      return params.data?.ROW_REF != null ? String(params.data.ROW_REF) : '';
    },
    onCellValueChanged: (event) => {
      event.api.refreshCells({ rowNodes: [event.node], force: true });
    },
    onGridReady: (grid) => {
      this.gridApi = grid.api;
      grid.api.addEventListener('rowSelected', this.rowClickEvent);
      grid.api.addEventListener('cellDoubleClicked', this.handleGridCellDblClick);
      grid.api.addEventListener('modelUpdated', this.initialise);
      this.statusRefreshInterval = setInterval(() => {
        if (this.gridApi) {
          this.gridApi.refreshCells({ columns: ['STATUS'], force: true });
        }
      }, 15000);
      this.calculateTotalQuantity();
    },
    onModelUpdated: (params) => {
      this.calculateTotalQuantity();
    },
    statusBar: {
      statusPanels: [
        {
          statusPanel: LabelValueStatusBarComponent,
          key: 'labelValue',
          align: 'left',
          statusPanelParams: {
            label: 'Total Quantity',
            value: 0,
            hide: false,
          },
        },
      ],
    },
  };

  private calculateTotalQuantity() {
    if (!this.gridApi) return;

    let totalQuantity = 0;

    (this.gridApi as any).forEachNodeAfterFilterAndSort?.((rowNode: any) => {
      if (!rowNode.group && rowNode.data && rowNode.data.IOI_SIZE != null) {
        const qty = parseFloat(rowNode.data.IOI_SIZE) || 0;
        totalQuantity += qty;
      }
    });

    const statusBarLabelValue = this.gridApi.getStatusPanel(GridProStatusBarTypes.labelValue);
    if (statusBarLabelValue) {
      (statusBarLabelValue as any).updateParams?.({
        value: formatNumeric(totalQuantity),
      });
    }
  }

  private initialise = () => {};

  private rowClickEvent = (e) => {};

  private handleGridCellDblClick = (e) => {
    if (e && e.data && e.data.CUSIP) {
      this.selectedBondId = e.data.BOND_ID;
    }
    this.trancheDetail.setRowData(e.data);
    this.bondDetailsOpen = true;
  };

  handleFlyoutClosed() {
    this.bondDetailsOpen = false;
  }

  disconnectedCallback() {
    if (this.statusRefreshInterval) {
      clearInterval(this.statusRefreshInterval);
      this.statusRefreshInterval = null;
    }
    super.disconnectedCallback?.();
  }

  @observable bidModeCriteria: CriteriaSegmentedControl;
  @observable timeModeCriteria: CriteriaSegmentedControl;
  @observable watchlistCriteria: CriteriaSegmentedControl;

  public clearAllFilters() {
    const userName = this.auth?.currentUser?.userName || 'anonymous';
    this.isRestoringFilters = true;
    this.cusipFilterValue = '';
    this.referenceFilterValue = '';
    this.issuerFilterValue = '';
    this.traderFilterValue = '';
    this.activityCriteria.selectedLabel = undefined;
    this.activityCriteriaValue = '';
    this.filterValue = '';
    this.isRestoringFilters = false;
    clearStoredFilters(userName);
  }
}
